import {
  type BigDecimal,
  createBigDecimal,
  divide,
  greaterThan,
  minus,
  plus,
  times,
} from '@0xtorch/big-decimal'
import type { AssetBalance } from '../../../types/assetBalance'
import type { AssetBorrow } from '../../../types/assetBorrow'
import type { CostBasis } from '../../../types/costBasis'

type AddBalanceToAssetBorrowsAndAssetBalancesParameters = {
  readonly amount: BigDecimal
  readonly value: BigDecimal
  readonly assetBalances: readonly AssetBalance[]
  readonly assetBorrows: readonly AssetBorrow[]
  readonly costBasis: CostBasis
}

type AddBalanceToAssetBorrowsAndAssetBalancesReturnTypes = {
  readonly assetBalances: readonly AssetBalance[]
  readonly assetBorrows: readonly AssetBorrow[]
}

export const addBalanceToAssetBorrowsAndAssetBalances = ({
  amount,
  value,
  assetBalances,
  assetBorrows,
  costBasis,
}: AddBalanceToAssetBorrowsAndAssetBalancesParameters): AddBalanceToAssetBorrowsAndAssetBalancesReturnTypes => {
  let remainAmount = amount
  let remainValue = value

  // amount < borrowed amount の asset borrow の amount を borrowed amount まで増やす
  for (const assetBorrow of assetBorrows) {
    if (greaterThan(assetBorrow.borrowedAmount, assetBorrow.amount)) {
      const diff = minus(assetBorrow.borrowedAmount, assetBorrow.amount)

      // remainAmount <= diff の場合
      // - amount に remainAmount 追加
      // - value に remainValue 追加
      // - remainAmount, remainValue 0 にする
      // - 処理終了
      if (!greaterThan(remainAmount, diff)) {
        assetBorrow.amount = plus(assetBorrow.amount, remainAmount)
        assetBorrow.value = plus(assetBorrow.value, remainValue)
        remainAmount = createBigDecimal(0n)
        remainValue = createBigDecimal(0n)
        break
      }

      // remainAmount > diff の場合
      // - amount に diff 追加
      // - value に remainValue * diff / remainAmount 追加
      // - remainAmount から diff を引く
      // - remainValue から remainValue * diff / remainAmount を引く
      const valueToAdd = divide(times(remainValue, diff, 6), remainAmount, 6)
      assetBorrow.amount = plus(assetBorrow.amount, diff)
      assetBorrow.value = plus(assetBorrow.value, valueToAdd)
      remainAmount = minus(remainAmount, diff)
      remainValue = minus(remainValue, valueToAdd)
    }
  }

  // remainAmount = 0 だったら asset balance には追加せず値を返す
  if (remainAmount.value === 0n) {
    return {
      assetBalances,
      assetBorrows,
    }
  }

  // cost basis に応じて asset balance に balance (amount, value) 追加して値を返す
  switch (costBasis) {
    case 'ACB':
    case 'WACB': {
      const assetBalance =
        assetBalances.length === 1
          ? assetBalances[0]
          : assetBalances.reduce(
              (acc, cur) => ({
                amount: plus(acc.amount, cur.amount),
                value: plus(acc.value, cur.value),
              }),
              { amount: createBigDecimal(0n), value: createBigDecimal(0n) },
            )
      assetBalance.amount = plus(assetBalance.amount, remainAmount)
      assetBalance.value = plus(assetBalance.value, remainValue)
      return {
        assetBalances: [assetBalance],
        assetBorrows,
      }
    }
    case 'FIFO': {
      return {
        assetBalances: [
          ...assetBalances,
          {
            amount: remainAmount,
            value: remainValue,
          },
        ],
        assetBorrows,
      }
    }
    case 'LIFO': {
      return {
        assetBalances: [
          {
            amount: remainAmount,
            value: remainValue,
          },
          ...assetBalances,
        ],
        assetBorrows,
      }
    }
    default: {
      throw new Error(`Unknown cost basis: ${costBasis satisfies never}`)
    }
  }
}
